// src/app/shared/services/audio.service.ts

import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class AudioService {
  private audio: HTMLAudioElement | null = null;
  private ringingTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private playbackToken: number = 0;

  private readonly ringingPauseMs: number = 2000;
  private readonly metadataTimeoutMs: number = 500;
  private readonly fallbackToneDurationMs: number = 450;

  private readonly ringingSoundPath: string = 'assets/sounds/phone-ringing.mp3';
  private readonly droppedToneSoundPath: string = 'assets/sounds/a-short-clear-sound-of-a-dropped-dial-tone.mp3';

  public playRingingSound(): void {
    this.stopSound();
    const token: number = this.playbackToken;
    this.playRingingLoop(token);
  }

  public playConnectingSound(): void {
    void this.playSingleTone();
  }

  public playDisconnectedSound(): void {
    void this.playSingleTone();
  }

  public playFailedSound(): void {
    void this.playFailedSoundWithDuration();
  }

  public async playFailedSoundWithDuration(): Promise<number> {
    return this.playSingleTone();
  }

  public stopSound(): void {
    this.playbackToken += 1;
    if (this.ringingTimeoutId) {
      clearTimeout(this.ringingTimeoutId);
      this.ringingTimeoutId = null;
    }

    if (!this.audio) return;

    this.audio.pause();
    this.audio.src = '';
    this.audio = null;
  }

  private async playSingleTone(): Promise<number> {
    this.stopSound();
    const token: number = this.playbackToken;
    const toneAudio: HTMLAudioElement = this.createAudio(this.droppedToneSoundPath);
    this.audio = toneAudio;

    const durationMs: number = await this.resolveDurationMs(toneAudio, this.fallbackToneDurationMs);
    if (token !== this.playbackToken || this.audio !== toneAudio) {
      return durationMs;
    }

    this.safePlay(toneAudio);
    return durationMs;
  }

  private playRingingLoop(token: number): void {
    if (token !== this.playbackToken) return;

    const ringingAudio: HTMLAudioElement = this.createAudio(this.ringingSoundPath);
    this.audio = ringingAudio;

    ringingAudio.addEventListener('ended', (): void => {
      if (token !== this.playbackToken) return;
      this.ringingTimeoutId = setTimeout((): void => {
        this.playRingingLoop(token);
      }, this.ringingPauseMs);
    }, { once: true });

    this.safePlay(ringingAudio);
  }

  private createAudio(soundPath: string): HTMLAudioElement {
    const audioElement: HTMLAudioElement = new Audio(soundPath);
    audioElement.preload = 'auto';
    return audioElement;
  }

  private safePlay(audioElement: HTMLAudioElement): void {
    audioElement.play().catch((error: unknown): void => {
      console.error('Audio play failed:', error);
    });
  }

  private async resolveDurationMs(audioElement: HTMLAudioElement, fallbackMs: number): Promise<number> {
    const currentDurationMs: number | null = this.getFiniteDurationMs(audioElement);
    if (currentDurationMs !== null) {
      return currentDurationMs;
    }

    await this.waitForMetadata(audioElement, this.metadataTimeoutMs);
    const loadedDurationMs: number | null = this.getFiniteDurationMs(audioElement);
    if (loadedDurationMs !== null) {
      return loadedDurationMs;
    }

    return fallbackMs;
  }

  private getFiniteDurationMs(audioElement: HTMLAudioElement): number | null {
    const durationSeconds: number = audioElement.duration;
    if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return null;
    return Math.ceil(durationSeconds * 1000);
  }

  private async waitForMetadata(audioElement: HTMLAudioElement, timeoutMs: number): Promise<void> {
    await new Promise<void>((resolve: () => void): void => {
      let finished: boolean = false;

      const finish = (): void => {
        if (finished) return;
        finished = true;
        clearTimeout(timeoutId);
        audioElement.removeEventListener('loadedmetadata', onLoadedMetadata);
        audioElement.removeEventListener('error', onError);
        resolve();
      };

      const onLoadedMetadata = (): void => finish();
      const onError = (): void => finish();
      const timeoutId: ReturnType<typeof setTimeout> = setTimeout((): void => finish(), timeoutMs);

      audioElement.addEventListener('loadedmetadata', onLoadedMetadata);
      audioElement.addEventListener('error', onError);

      try {
        audioElement.load();
      } catch {
        finish();
      }
    });
  }
}
